import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import softwareDevImg from '../assets/services/software-development.png'

/* =========================================================================
   ServiceCard — image-led card for the Services index.
   Each service gets a thematic header image (local where available,
   curated Unsplash otherwise), a gradient overlay, an icon badge anchored
   to the bottom-left of the image, and a hover zoom. The category still
   drives the tint/ring/badge colour, it just isn't labelled on the card.
   ========================================================================= */

/* Per-slug image map. Local /images first; Unsplash as fallback for slugs
   we don't have local art for. */
const IMAGE_BY_SLUG = {
  'software-development':     softwareDevImg,
  'website-development':      '/images/website-development.png',
  'ui-ux-design':             '/images/ui1.jpg',
  'drone-development':        '/images/uav.webp',
  'iot-development':          '/images/remote/unsplash-7a19be13c2.jpg',
  'app-development':          '/images/remote/unsplash-925aeb815d.jpg',
  'cyber-security':           '/images/remote/unsplash-f151359a61.jpg',
  'digital-marketing':        '/images/remote/unsplash-7177ed96d7.jpg',
  'social-media-management':  '/images/remote/unsplash-66f4f1ef29.jpg',
  'business-consulting':      '/images/remote/unsplash-479be9e212.jpg',
  'ai-automation':            '/images/remote/unsplash-24bab4753b.jpg',
}

/* Per-category gradient tint — sits over the image to keep the card family
   visually unified despite varied photography. */
const CATEGORY_GRADIENT = {
  Engineering:  'linear-gradient(180deg, rgba(8,12,30,0.10) 30%, rgba(56,189,248,0.55) 100%)',
  Intelligence: 'linear-gradient(180deg, rgba(8,12,30,0.10) 30%, rgba(168,85,247,0.55) 100%)',
  Operations:   'linear-gradient(180deg, rgba(8,12,30,0.10) 30%, rgba(34,197,94,0.55) 100%)',
  Security:     'linear-gradient(180deg, rgba(8,12,30,0.10) 30%, rgba(239,68,68,0.55) 100%)',
  Growth:       'linear-gradient(180deg, rgba(8,12,30,0.10) 30%, rgba(244,114,182,0.55) 100%)',
  Advisory:     'linear-gradient(180deg, rgba(8,12,30,0.10) 30%, rgba(251,191,36,0.55) 100%)',
}

const CATEGORY_RING = {
  Engineering:  'ring-sky-400/30',
  Intelligence: 'ring-violet-400/30',
  Operations:   'ring-emerald-400/30',
  Security:     'ring-rose-400/30',
  Growth:       'ring-pink-400/30',
  Advisory:     'ring-amber-400/30',
}

const CATEGORY_BADGE = {
  Engineering:  'bg-sky-500',
  Intelligence: 'bg-violet-500',
  Operations:   'bg-emerald-500',
  Security:     'bg-rose-500',
  Growth:       'bg-pink-500',
  Advisory:     'bg-amber-500',
}

export default function ServiceCard({ service }) {
  const Icon = service.icon
  const image = IMAGE_BY_SLUG[service.slug] ||
    '/images/remote/unsplash-4593053287.jpg'
  const overlay = CATEGORY_GRADIENT[service.category] || CATEGORY_GRADIENT.Engineering
  const ring = CATEGORY_RING[service.category] || CATEGORY_RING.Engineering
  const badgeBg = CATEGORY_BADGE[service.category] || CATEGORY_BADGE.Engineering

  return (
    <Link
      to={`/services/${service.slug}`}
      className={
        'card group relative flex flex-col overflow-hidden p-0 transition-all hover:-translate-y-1 hover:shadow-2xl hover:ring-2 ' +
        ring
      }
    >
      {/* Image header */}
      <div className="relative h-48 bg-slate-900">
        <div className="relative h-full w-full overflow-hidden">
          <img
            src={image}
            alt={service.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          {/* Category-tinted gradient overlay */}
          <div aria-hidden className="absolute inset-0" style={{ background: overlay }} />
        </div>
        {/* Icon badge — anchored bottom-left of image, z-20 sits on top */}
        <span
          className={
            'absolute -bottom-5 left-4 z-20 grid h-12 w-12 place-items-center rounded-xl text-white shadow-lg ring-4 ring-ink-950 ' +
            badgeBg
          }
        >
          {Icon ? <Icon className="h-5 w-5" /> : null}
        </span>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-1 flex-col p-6 pt-8">
        <h3 className="text-lg font-bold tracking-tight text-white">{service.title}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{service.summary}</p>
        <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-300 transition-all group-hover:gap-2 group-hover:text-brand-200">
          Learn more <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  )
}
